<script setup>
import { ref } from 'vue';

const props = defineProps({
  controller: { type: Object, required: true },
});

const fileInput = ref(null);
const isDragging = ref(false);

const openPicker = () => {
  fileInput.value?.click();
};

const onFileChange = async event => {
  await props.controller.uploadImages(event.target.files);
  if (event.target) event.target.value = '';
};

const onDrop = async event => {
  isDragging.value = false;
  await props.controller.uploadImages(event.dataTransfer?.files);
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="mx-auto flex w-full max-w-3xl flex-col gap-4">
    <p class="text-[13px] text-lais-text-muted">
      Adicione as fotos do imóvel. A primeira foto é usada como capa do anúncio.
      Você pode reordenar e remover antes de publicar.
    </p>

    <!-- Área de upload -->
    <button
      type="button"
      class="flex flex-col items-center justify-center gap-2 rounded-lais border-2 border-dashed px-4 py-10 text-center transition-colors"
      :class="
        isDragging
          ? 'border-lais-primary bg-lais-primary-50/40'
          : 'border-lais-border bg-lais-bg-soft hover:border-lais-primary'
      "
      :disabled="controller.isUploadingImages.value"
      @click="openPicker"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <span
        class="i-lucide-image-up size-7 text-lais-primary"
        aria-hidden="true"
      />
      <span class="text-[14px] font-semibold text-lais-text-body">
        {{
          controller.isUploadingImages.value
            ? 'Enviando fotos...'
            : 'Clique ou arraste fotos aqui'
        }}
      </span>
      <span class="text-[12px] text-lais-text-muted">
        JPG, PNG ou WEBP. Selecione várias de uma vez.
      </span>
    </button>

    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      multiple
      class="hidden"
      @change="onFileChange"
    />

    <!-- Galeria -->
    <div
      v-if="controller.state.selectedImages.length"
      class="flex items-center justify-between"
    >
      <span class="text-[13px] font-semibold text-lais-text-body">
        Galeria ({{ controller.state.selectedImages.length }})
      </span>
      <span class="text-[12px] text-lais-text-muted">
        A capa é a primeira imagem
      </span>
    </div>

    <div
      v-if="controller.state.selectedImages.length"
      class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
    >
      <div
        v-for="(url, index) in controller.state.selectedImages"
        :key="url"
        class="group relative aspect-square overflow-hidden rounded-lais-sm border border-lais-border bg-lais-bg-soft"
        :class="index === 0 ? 'ring-2 ring-lais-primary' : ''"
      >
        <img
          :src="url"
          alt="Foto do imóvel"
          class="h-full w-full object-cover"
        />
        <span
          v-if="index === 0"
          class="absolute left-1.5 top-1.5 rounded-full bg-lais-primary px-2 py-0.5 text-[10px] font-bold text-white"
        >
          Capa
        </span>
        <div
          class="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 bg-black/40 p-1 opacity-0 transition-opacity group-hover:opacity-100"
        >
          <button
            v-if="index !== 0"
            type="button"
            title="Tornar capa"
            class="flex size-7 items-center justify-center rounded-full bg-white/90 text-lais-text hover:bg-white"
            @click="controller.moveImageToCover(url)"
          >
            <span class="i-lucide-star size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            title="Remover"
            class="flex size-7 items-center justify-center rounded-full bg-white/90 text-lais-danger hover:bg-white"
            @click="controller.removeImage(url)"
          >
            <span class="i-lucide-trash-2 size-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>

    <div
      v-else
      class="rounded-lais bg-lais-bg-soft px-4 py-6 text-center text-[13px] text-lais-text-muted"
    >
      Nenhuma foto adicionada ainda.
    </div>
  </div>
</template>
